import { useState } from "react";
import { Card } from "components/shadcn/ui/card";
import { Button } from "components/shadcn/ui/button";
import { Badge } from "components/shadcn/ui/badge";
import { Input } from "components/shadcn/ui/input";
import { X } from "lucide-react";
import clsx from "clsx";
import { EMAIL_REGEX } from "components/common/helper/validationSchema";

interface InviteMembersProps {
  open: boolean;
  loading?: boolean;
  onCancel: () => void;
  onInvite: (emails: string[]) => Promise<void>;
}

const InviteMembers: React.FC<InviteMembersProps> = ({
  open,
  loading,
  onCancel,
  onInvite,
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  const addEmail = (raw: string) => {
    const email = raw.trim().replace(/,$/, "");

    if (!EMAIL_REGEX.test(email)) {
      setError("Invalid email address");
      return;
    }

    if (emails.includes(email)) {
      setError("Email already added");
      return;
    }

    setEmails((prev) => [...prev, email]);
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["Enter", " ", ","].includes(e.key)) {
      e.preventDefault();
      if (input) {
        addEmail(input);
        setInput("");
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .split(/[,\s]+/)
      .filter(Boolean);

    pasted.forEach(addEmail);
  };

  const sendInvites = async () => {
    const finalEmails = [...emails];

    if (input) {
      const email = input.trim().replace(/,$/, "");
      if (EMAIL_REGEX.test(email) && !finalEmails.includes(email)) {
        finalEmails.push(email);
      }
    }

    if (!finalEmails.length) return;

    await onInvite(finalEmails);

    setEmails([]);
    setInput("");
    setError(null);
  };

  return (
    <div
      className={clsx(
        "overflow-hidden transition-all duration-300",
        open ? "max-h-[500px] mt-4" : "max-h-0"
      )}
    >
      <Card className="p-4 space-y-3">
        <div className="space-y-1">
          <p className="font-semibold text-gray-700">Invite new members</p>
          <p className="text-sm text-gray-400">
            Enter or paste email addresses separated by spaces or commas.
          </p>
        </div>

        {emails.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {emails.map((email) => (
              <Badge
                key={email}
                variant="outline"
                className="flex items-center gap-1 max-w-full bg-[--primary-600] text-white border-red-400"
              >
                <span className="truncate max-w-[240px]">{email}</span>
                <X
                  className="h-3 w-3 cursor-pointer shrink-0"
                  onClick={() => {
                    setEmails((prev) => prev.filter((e) => e !== email));
                    setError(null);
                  }}
                />
              </Badge>
            ))}
          </div>
        )}

        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder="email@example.com"
          disabled={loading}
        />

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={loading || (!emails.length && !input)}
            onClick={sendInvites}
          >
            {loading ? "Sending..." : "Send invitations"}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default InviteMembers;
